import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { LayoutDashboard, Package, ArrowLeftRight, ShoppingCart, FileDown, Users, LogOut, Boxes, Tags, Wallet, CalendarRange, Clock, History, Coins, Wrench } from "lucide-react";
import InstallButton from "./InstallButton";

const nav = [
  { to: "/", label: "Dasbor", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/barang", label: "Stok", icon: Package, testid: "nav-items", perm: "stok" },
  { to: "/harga", label: "Harga", icon: Tags, testid: "nav-prices", perm: "prices" },
  { to: "/hpp", label: "HPP", icon: Coins, testid: "nav-hpp", perm: "modal" },
  { to: "/transaksi", label: "Mutasi Stok", icon: ArrowLeftRight, testid: "nav-transactions", perm: "mutasi" },
  { to: "/penjualan", label: "Penjualan", icon: ShoppingCart, testid: "nav-sales", perm: "penjualan" },
  { to: "/rusak", label: "Barang Rusak", icon: Wrench, testid: "nav-damaged", perm: "rusak" },
  { to: "/hutang", label: "Hutang", icon: Wallet, testid: "nav-debts", perm: "hutang" },
  { to: "/absensi", label: "Absensi", icon: Clock, testid: "nav-attendance", perm: "absensi" },
  { to: "/laporan", label: "Laporan", icon: FileDown, testid: "nav-reports", perm: "laporan" },
  { to: "/laporan-bulanan", label: "Bulanan", icon: CalendarRange, testid: "nav-monthly", perm: "monthly" },
  { to: "/pengguna", label: "Pengguna", icon: Users, testid: "nav-users", ownerOnly: true },
  { to: "/log-aktivitas", label: "Log", icon: History, testid: "nav-logs", ownerOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = nav.filter((n) => {
    if (n.ownerOnly) return user?.is_owner;
    if (n.perm) return user?.is_owner || user?.perms?.[n.perm];
    return true;
  });

  const doLogout = async () => { await logout(); navigate("/login"); };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg font-semibold border-2 transition-transform duration-150 ${
      isActive ? "border-[#0A0A0A] nb-shadow" : "border-transparent hover:border-[#0A0A0A]"
    }`;

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col fixed left-0 top-0 h-full w-64 border-r-2 border-[#0A0A0A] bg-white p-5">
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-11 h-11 border-2 border-[#0A0A0A] rounded-xl flex items-center justify-center nb-shadow" style={{ background: "var(--primary)" }}>
            <Boxes className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-extrabold text-lg leading-none">Mainan Dede</div>
            <div className="label-tag mt-0.5">Inventori</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1.5 flex-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={linkCls}
              style={({ isActive }) => (isActive ? { background: "var(--primary)" } : {})}>
              <n.icon className="w-5 h-5" strokeWidth={2.3} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t-2 border-[#0A0A0A] pt-4 mt-4">
          <div className="text-sm font-bold truncate">{user?.name}</div>
          <div className="label-tag mb-3">{user?.is_owner ? "owner" : user?.role}</div>
          <InstallButton className="w-full py-2 mb-2 text-[#0A0A0A] text-sm" />
          <button data-testid="logout-button" onClick={doLogout} className="nb-btn w-full py-2 flex items-center justify-center gap-2 bg-white text-sm">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b-2 border-[#0A0A0A] bg-white">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 border-2 border-[#0A0A0A] rounded-lg flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Boxes className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <span className="font-display font-extrabold text-lg">Mainan Dede</span>
        </div>
        <div className="flex items-center gap-2">
          <InstallButton className="px-3 py-1.5 text-[#0A0A0A] text-sm" />
          <button data-testid="logout-button-mobile" onClick={doLogout} className="nb-btn px-3 py-1.5 bg-white text-sm flex items-center gap-1.5">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="md:ml-64 p-4 sm:p-6 pb-28 md:pb-6 max-w-6xl">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 w-full z-50 bg-white border-t-2 border-[#0A0A0A] flex overflow-x-auto px-1 py-1.5 gap-1">
        {items.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`${n.testid}-mobile`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-2.5 py-1 rounded-md text-[10px] font-bold shrink-0 min-w-[62px] text-center ${isActive ? "" : "text-[#52525B]"}`}
            style={({ isActive }) => (isActive ? { background: "var(--primary)", border: "2px solid #0A0A0A" } : {})}>
            <n.icon className="w-5 h-5" strokeWidth={2.3} /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
